// ═══════════════════════════════════════════════════════════════════════════════
// UI language context (zh-Hant / zh-Hans / en / id / fil)
// ═══════════════════════════════════════════════════════════════════════════════

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { LANGUAGE, SUPPORTED_LANGUAGES, type Language } from '@fdh/shared';
import { useAuth } from './AuthContext';
import {
  UI_MESSAGES,
  formatMessage,
  type MessageParams,
} from '../i18n/uiMessages';

const LANGUAGE_STORAGE_KEY = 'fdh-ui-language';

const isValidLanguage = (value: string | undefined | null): value is Language =>
  value != null && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);

/** API may return legacy codes (e.g. zh-HK from DB seed). */
const normalizeApiLanguage = (raw: string | undefined | null): Language | null => {
  if (raw == null) return null;
  if (raw === 'zh-HK' || raw === 'zh-Hant') return LANGUAGE.ZH_HANT;
  if (raw === 'zh-CN' || raw === 'zh-Hans') return LANGUAGE.ZH_HANS;
  if (raw === 'en') return LANGUAGE.EN;
  if (raw === 'id' || raw === 'in') return LANGUAGE.ID;
  if (raw === 'fil' || raw === 'tl') return LANGUAGE.FIL;
  return isValidLanguage(raw) ? raw : null;
};

const readNavigatorLanguage = (): Language | null => {
  if (typeof navigator === 'undefined') return null;
  const nav = navigator.language?.toLowerCase() ?? '';
  if (
    nav.startsWith('zh-hk') ||
    nav.startsWith('zh-tw') ||
    nav.startsWith('zh-hant')
  ) {
    return LANGUAGE.ZH_HANT;
  }
  if (
    nav.startsWith('zh-cn') ||
    nav.startsWith('zh-hans') ||
    nav.startsWith('zh-sg')
  ) {
    return LANGUAGE.ZH_HANS;
  }
  if (nav === 'zh') {
    return LANGUAGE.ZH_HANT;
  }
  if (nav.startsWith('id') || nav === 'in') {
    return LANGUAGE.ID;
  }
  if (nav.startsWith('fil') || nav.startsWith('tl')) {
    return LANGUAGE.FIL;
  }
  if (nav.startsWith('en')) {
    return LANGUAGE.EN;
  }
  return null;
};

const readInitialLanguage = (): Language => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isValidLanguage(stored)) return stored;
  } catch {
    // ignore
  }
  return readNavigatorLanguage() ?? LANGUAGE.ZH_HANT;
};

const toDocumentLang = (language: Language): string => {
  if (language === LANGUAGE.EN) return 'en';
  if (language === LANGUAGE.ID) return 'id';
  if (language === LANGUAGE.FIL) return 'fil';
  return language;
};

type LanguageContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, params?: MessageParams) => string;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

export const useLanguage = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
};

/**
 * Same translation behaviour as `useLanguage().t`, but never throws when the tree is
 * temporarily outside LanguageProvider (e.g. HMR / edge layouts). Falls back to
 * localStorage / navigator language like the provider’s initial state.
 */
export const useTranslationSafe = (): LanguageContextValue['t'] => {
  const ctx = useContext(LanguageContext);
  const language = ctx?.language ?? readInitialLanguage();

  return useCallback(
    (key: string, params?: MessageParams) => {
      if (ctx) {
        return ctx.t(key, params);
      }
      const row = UI_MESSAGES[language];
      const raw = row[key] ?? UI_MESSAGES[LANGUAGE.EN][key];
      return formatMessage(raw, params) || key;
    },
    [ctx, language]
  );
};

type LanguageProviderProps = {
  children: ReactNode;
};

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const { user } = useAuth();
  const [language, setLanguageState] = useState<Language>(readInitialLanguage);

  useEffect(() => {
    const fromUser = normalizeApiLanguage(user?.settings?.language);
    if (fromUser) {
      setLanguageState(fromUser);
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, fromUser);
      } catch {
        // ignore
      }
    }
  }, [user?.settings?.language]);

  useEffect(() => {
    document.documentElement.lang = toDocumentLang(language);
  }, [language]);

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
    } catch {
      // ignore
    }
    document.documentElement.lang = toDocumentLang(next);
  }, []);

  const t = useCallback(
    (key: string, params?: MessageParams) => {
      const row = UI_MESSAGES[language];
      const raw = row[key] ?? UI_MESSAGES[LANGUAGE.EN][key];
      return formatMessage(raw, params) || key;
    },
    [language]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
    }),
    [language, setLanguage, t]
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};
